// MARK://dhk8/admin-shell/components/AvatarMenu
// Avatar (iniciales) en el topbar → menú de cuenta. Paridad con el AvatarMenu del
// POS: consolida lo que antes vivía suelto en el topbar del admin (toggle
// Pruebas/Vivo, tema, nombre, cerrar sesión) en un solo botón de iniciales.
// Cierra con click-fuera o Escape. Colores por --pos-* tokens.

import { useEffect, useRef, useState } from 'react'
import { LogOut, Sun, Moon } from 'lucide-react'

import { useAuthStore } from '../stores/auth-store.js'
import { useThemeStore } from '../stores/theme.store.js'

/** "Abraham Vazquez" → "AV"; "Cocina" → "CO"; vacío → "·". */
function initials(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase()
}

export default function AvatarMenu({ onLogout }: { onLogout: () => void }) {
  const session = useAuthStore((s) => s.session)
  const switchEnv = useAuthStore((s) => s.switchEnv)
  const theme = useThemeStore((s) => s.theme)
  const toggleTheme = useThemeStore((s) => s.toggleTheme)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey) }
  }, [open])

  if (!session) return null
  const isDark = theme === 'dark'
  const env = session.activeEnv ?? 'test'
  const hasLiveLink = !!session.liveTenantId && !!session.testTenantId && !session.impersonating
  const role = session.roles?.[0]

  function changeEnv(target: 'test' | 'live') {
    switchEnv(target).catch((err) => {
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(`No se pudo cambiar a ${target === 'test' ? 'Pruebas' : 'En Vivo'}: ${err instanceof Error ? err.message : String(err)}`)
      }
    })
  }

  return (
    <div className="adm-avatar" ref={ref}>
      <button
        type="button"
        className="adm-avatar__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={session.fullName}
        title={session.fullName}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="adm-avatar__initials">{initials(session.fullName)}</span>
      </button>

      {open && (
        <div className="adm-avatar__menu" role="menu">
          <div className="adm-avatar__head">
            <span className="adm-avatar__name">{session.fullName}</span>
            {role && <span className="adm-avatar__role">{role}</span>}
          </div>

          {hasLiveLink && (
            <div className="adm-avatar__env">
              <div className="adm-env-switch" role="group" aria-label="Entorno de datos">
                <button
                  type="button"
                  className={`adm-env-switch__opt${env === 'test' ? ' adm-env-switch__opt--active adm-env-switch__opt--test' : ''}`}
                  aria-pressed={env === 'test'}
                  onClick={() => changeEnv('test')}
                >Pruebas</button>
                <button
                  type="button"
                  className={`adm-env-switch__opt${env === 'live' ? ' adm-env-switch__opt--active adm-env-switch__opt--live' : ''}`}
                  aria-pressed={env === 'live'}
                  onClick={() => changeEnv('live')}
                >En vivo</button>
              </div>
            </div>
          )}

          <button type="button" className="adm-avatar__item" role="menuitem" onClick={() => toggleTheme()}>
            {isDark ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
            {isDark ? 'Tema claro' : 'Tema oscuro'}
          </button>

          <div className="adm-avatar__sep" />
          <button
            type="button"
            className="adm-avatar__item adm-avatar__item--danger"
            role="menuitem"
            onClick={() => { setOpen(false); onLogout() }}
          >
            <LogOut size={16} aria-hidden /> Cerrar sesión
          </button>
        </div>
      )}
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .adm-avatar { position: relative; flex-shrink: 0; }
  .adm-avatar__btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 36px; height: 36px; padding: 0; cursor: pointer;
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-radius: 50%;
    background: var(--pos-tint-fill, color-mix(in oklch, #1F8B3A 16%, transparent));
    color: var(--pos-tint-on, #E8EFE9);
    -webkit-tap-highlight-color: transparent;
    transition: border-color var(--pos-duration-fast) var(--pos-ease-out), transform var(--pos-duration-fast) var(--pos-ease-out);
  }
  .adm-avatar__btn:hover { border-color: var(--pos-tint, #1F8B3A); }
  .adm-avatar__btn:active { transform: scale(.95); }
  .adm-avatar__btn:focus-visible { outline: none; border-color: var(--pos-tint, #1F8B3A); box-shadow: 0 0 0 3px var(--pos-tint-soft); }
  .adm-avatar__initials { font-family: var(--font-display, var(--font-sans)); font-size: 13px; font-weight: var(--pos-weight-semibold, 600); letter-spacing: .02em; }

  .adm-avatar__menu {
    position: absolute; top: calc(100% + 8px); right: 0; z-index: var(--pos-z-toast, 300);
    min-width: 232px; display: flex; flex-direction: column; gap: 2px;
    padding: var(--pos-space-2, 8px);
    background: var(--pos-surface, #161C18);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-radius: var(--pos-radius-lg, 12px);
    box-shadow: var(--pos-shadow-4, 0 24px 60px -16px rgba(0,0,0,.55));
  }
  .adm-avatar__head { display: flex; flex-direction: column; gap: 1px; padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px); }
  .adm-avatar__name { font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-semibold, 600); color: var(--pos-fg, #E8EFE9); }
  .adm-avatar__role { font-size: var(--pos-text-xxs, 11px); text-transform: capitalize; color: var(--pos-fg-tertiary, #7E8C82); }
  .adm-avatar__env { display: flex; justify-content: center; padding: 2px var(--pos-space-2, 8px) var(--pos-space-2, 8px); }
  .adm-avatar__item {
    display: flex; align-items: center; gap: var(--pos-space-3, 12px);
    width: 100%; min-height: 40px; padding: 0 var(--pos-space-3, 12px);
    border: none; border-radius: var(--pos-radius-md, 8px);
    background: transparent; color: var(--pos-fg-secondary, #B6BFB8);
    font: inherit; font-size: var(--pos-text-sm, 13px); text-align: left; cursor: pointer;
    transition: background var(--pos-duration-fast) var(--pos-ease-out), color var(--pos-duration-fast) var(--pos-ease-out);
  }
  .adm-avatar__item:hover { background: var(--pos-elevated, #1E2520); color: var(--pos-fg, #E8EFE9); }
  .adm-avatar__item--danger { color: var(--pos-danger, #E5484D); }
  .adm-avatar__item--danger:hover { background: color-mix(in oklch, var(--pos-danger, #E5484D) 12%, transparent); color: var(--pos-danger, #E5484D); }
  .adm-avatar__sep { height: 1px; margin: var(--pos-space-1, 4px) var(--pos-space-2, 8px); background: var(--pos-separator, rgba(255,255,255,.07)); }
`
